import './App.css'

import { ClockIcon } from '@heroicons/react/outline'
import { format } from 'date-fns'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import { useEffect, useRef, useState } from 'react'
import { Routes, Route } from 'react-router-dom'

import { AlertContainer } from './components/alerts/AlertContainer'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { AboutModal } from './components/modals/AboutModal'
import { DatePickerModal } from './components/modals/DatePickerModal'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { Admin } from './components/Admin'
import { LockdownScreen } from './components/LockdownScreen'
import {
  DATE_LOCALE,
  DISCOURAGE_INAPP_BROWSERS,
  LONG_ALERT_TIME_MS,
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import {
  CORRECT_WORD_MESSAGE,
  DISCOURAGE_INAPP_BROWSER_TEXT,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  SHARE_FAILURE_TEXT,
  WIN_MESSAGES,
  WORD_NOT_FOUND_MESSAGE,
} from './constants/strings'
import { useAlert } from './context/AlertContext'
import { useGame } from './context/GameContext'
import { isInAppBrowser } from './lib/browser'
import {
  getStoredIsHighContrastMode,
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
} from './lib/localStorage'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  findFirstUnusedReveal,
  getGameDate,
  getIsLatestGame,
  isWinningWord,
  isWordInWordList,
  setGameDate,
  unicodeLength,
} from './lib/words'

function App() {
  const { solution, solutionGameDate, tomorrow, isLoading, error, isLocked } = useGame()
  const isLatestGame = getIsLatestGame()
  const gameDate = getGameDate()

  const { showError: showErrorAlert, showSuccess: showSuccessAlert } =
    useAlert()

  // All hooks must be called before any early returns
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isDatePickerModalOpen, setIsDatePickerModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [isHighContrastMode] = useState(getStoredIsHighContrastMode())
  const [isRevealing, setIsRevealing] = useState(false)
  // Start with empty guesses — restored from localStorage once solution is loaded (see useEffect below)
  const [guesses, setGuesses] = useState<string[]>([])
  const hasRestoredFromStorage = useRef(false)

  const [stats, setStats] = useState(() => loadStats())

  const [isHardMode] = useState(
    localStorage.getItem('gameMode')
      ? localStorage.getItem('gameMode') === 'hard'
      : false
  )

  // Restore game state from localStorage once solution is loaded from Firebase.
  // This runs when solution transitions from null -> a real word.
  // The lazy initializer approach doesn't work because solution is null on first render.
  useEffect(() => {
    if (!solution || hasRestoredFromStorage.current) return
    hasRestoredFromStorage.current = true

    const loaded = loadGameStateFromLocalStorage(isLatestGame)
    if (!loaded || loaded.solution !== solution) {
      // No saved state for today's word — show how-to modal
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
      return
    }

    // Restore guesses
    setGuesses(loaded.guesses)

    const gameWasWon = loaded.guesses.includes(solution)
    if (gameWasWon) {
      setIsGameWon(true)
    } else if (loaded.guesses.length === MAX_CHALLENGES) {
      setIsGameLost(true)
      showErrorAlert(CORRECT_WORD_MESSAGE(solution), {
        persist: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solution])

  useEffect(() => {
    DISCOURAGE_INAPP_BROWSERS &&
      isInAppBrowser() &&
      showErrorAlert(DISCOURAGE_INAPP_BROWSER_TEXT, {
        persist: false,
        durationMs: 7000,
      })
  }, [showErrorAlert])

  useEffect(() => {
    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isHighContrastMode])

  useEffect(() => {
    if (solution) {
      saveGameStateToLocalStorage(getIsLatestGame(), { guesses, solution })
    }
  }, [guesses, solution])

  useEffect(() => {
    if (solution && isGameWon) {
      const winMessage =
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      const delayMs = REVEAL_TIME_MS * solution.length

      showSuccessAlert(winMessage, {
        delayMs,
        onClose: () => setIsStatsModalOpen(true),
      })
    }

    if (solution && isGameLost) {
      setTimeout(
        () => {
          setIsStatsModalOpen(true)
        },
        (solution.length + 1) * REVEAL_TIME_MS
      )
    }
  }, [isGameWon, isGameLost, showSuccessAlert, solution])

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  // Check if user is on admin route
  const isAdminRoute = window.location.hash === '#/occ-admin'

  // Show lockdown screen if game is locked and not on admin route
  if (!isLoading && isLocked && !isAdminRoute) {
    return <LockdownScreen />
  }

  // Show loading state while fetching word from Firebase
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clash-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading today's word...</p>
        </div>
      </div>
    )
  }

  // Show error state if word loading failed
  if (error || !solution) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading game. Please refresh the page.</p>
        </div>
      </div>
    )
  }

  const onChar = (value: string) => {
    if (
      unicodeLength(`${currentGuess}${value}`) <= solution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuess(`${currentGuess}${value}`)
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }

    if (!(unicodeLength(currentGuess) === solution.length)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    if (!isWordInWordList(currentGuess)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (isHardMode) {
      const firstMissingReveal = findFirstUnusedReveal(currentGuess, guesses, solution)
      if (firstMissingReveal) {
        setCurrentRowClass('jiggle')
        return showErrorAlert(firstMissingReveal, {
          onClose: clearCurrentRowClass,
        })
      }
    }

    setIsRevealing(true)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
    }, REVEAL_TIME_MS * solution.length)

    const winningWord = isWinningWord(currentGuess, solution)

    if (
      unicodeLength(currentGuess) === solution.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        if (isLatestGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length))
        }
        return setIsGameWon(true)
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        if (isLatestGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        }
        setIsGameLost(true)
        showErrorAlert(CORRECT_WORD_MESSAGE(solution), {
          persist: true,
          delayMs: REVEAL_TIME_MS * solution.length + 1,
        })
      }
    }
  }

  return (
    <Routes>
      <Route path="/occ-admin" element={<Admin />} />
      <Route
        path="*"
        element={
          <div className="flex min-h-screen flex-col">
            <Header
              setIsInfoModalOpen={(open) => {
                // Only allow How To Play when game is not over
                if (open && (isGameWon || isGameLost)) return
                setIsInfoModalOpen(open)
              }}
              setIsAboutModalOpen={setIsAboutModalOpen}
              setIsStatsModalOpen={setIsStatsModalOpen}
              setIsDatePickerModalOpen={setIsDatePickerModalOpen}
            />

            {!isLatestGame && (
              <div className="flex items-center justify-center gap-2 bg-slate-100 py-2">
                <ClockIcon className="h-5 w-5 stroke-gray-600" />
                <p className="text-sm text-gray-600">
                  {format(gameDate, 'd MMMM yyyy', { locale: DATE_LOCALE })}
                </p>
              </div>
            )}

            <div className="mx-auto flex min-h-[calc(100vh-72px)] w-full max-w-xl flex-col justify-center gap-5 px-4 py-5 sm:px-6">
              <div className="flex grow flex-col justify-center pb-6 short:pb-2">
                <Grid
                  solution={solution}
                  guesses={guesses}
                  currentGuess={currentGuess}
                  isRevealing={isRevealing}
                  currentRowClassName={currentRowClass}
                />
              </div>
              <Keyboard
                onChar={onChar}
                onDelete={onDelete}
                onEnter={onEnter}
                solution={solution}
                guesses={guesses}
                isRevealing={isRevealing}
              />
              <InfoModal
                isOpen={isInfoModalOpen}
                handleClose={() => setIsInfoModalOpen(false)}
              />
              <AboutModal
                isOpen={isAboutModalOpen}
                handleClose={() => setIsAboutModalOpen(false)}
              />
              <StatsModal
                isOpen={isStatsModalOpen}
                handleClose={() => setIsStatsModalOpen(false)}
                solution={solution}
                guesses={guesses}
                gameStats={stats}
                isLatestGame={isLatestGame}
                isGameLost={isGameLost}
                isGameWon={isGameWon}
                handleShareToClipboard={() =>
                  showSuccessAlert(GAME_COPIED_MESSAGE)
                }
                handleShareFailure={() =>
                  showErrorAlert(SHARE_FAILURE_TEXT, {
                    durationMs: LONG_ALERT_TIME_MS,
                  })
                }
                isHardMode={isHardMode}
                isDarkMode={false}
                isHighContrastMode={isHighContrastMode}
                numberOfGuessesMade={guesses.length}
                solutionGameDate={solutionGameDate}
                tomorrow={tomorrow}
              />
              <DatePickerModal
                isOpen={isDatePickerModalOpen}
                initialDate={solutionGameDate}
                handleSelectDate={(d) => {
                  setIsDatePickerModalOpen(false)
                  setGameDate(d)
                }}
                handleClose={() => setIsDatePickerModalOpen(false)}
              />
              <AlertContainer />
            </div>

            <Footer />
          </div>
        }
      />
    </Routes>
  )
}

export default App
